import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { clientService } from '../../services';

// Plan details for displaying expected plan info while webhook processes
const GST_RATE = 18;
const PLAN_DETAILS = {
  free: { name: 'Free Trial', monthlyPrice: 0 },
  pro: { name: 'Pro', monthlyPrice: 1999 },
  pro_plus: { name: 'Pro Plus', monthlyPrice: 5999 },
  enterprise_starter: { name: 'Enterprise Starter', yearlyPrice: 180000 },
  enterprise_scale: { name: 'Enterprise Scale', yearlyPrice: 0 },
};

/**
 * Checkout Success Page
 * Displayed after successful payment from Dodo checkout
 */

const CheckoutSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const syncAttempted = useRef(false);

  // Get payment details from URL params (Dodo sends these back)
  const paymentId = searchParams.get('payment_id');
  const status = searchParams.get('status');
  const planId = searchParams.get('plan_id') || localStorage.getItem('pending_plan_id');
  const billingCycle = searchParams.get('billing_cycle') || localStorage.getItem('pending_billing_cycle') || 'monthly';

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        setLoading(true);
        // Give webhook time to process
        await new Promise(resolve => setTimeout(resolve, 2000));

        let response = await clientService.getSubscription();
        if (response.success && response.data) {
          const fetchedPlan = response.data.plan || response.data.tier;
          // If the fetched plan doesn't match the expected plan, auto-sync
          if (planId && fetchedPlan !== planId && !syncAttempted.current) {
            syncAttempted.current = true;
            try {
              const syncResponse = await clientService.syncSubscription(planId, billingCycle, paymentId);
              if (syncResponse.success) {
                setSubscription(syncResponse.data.subscription);
                setSyncSuccess(true);
                setError(null);
                return;
              }
            } catch (syncErr) {
              console.error('Auto-sync failed:', syncErr);
              // Sync failed (likely webhook already processed) — re-fetch fresh data
              try {
                response = await clientService.getSubscription();
                if (response.success && response.data) {
                  setSubscription(response.data);
                  return;
                }
              } catch {
                // Fall through to use original response
              }
            }
          }
          setSubscription(response.data);
        }
      } catch (err) {
        console.error('Error fetching subscription:', err);
        setError('Unable to load subscription details');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
    // Clear pending plan data
    localStorage.removeItem('pending_plan_id');
    localStorage.removeItem('pending_billing_cycle');
  }, [planId, billingCycle, paymentId]);

  const handleManualSync = async () => {
    if (!planId) {
      setError('No plan information available for sync');
      return;
    }

    try {
      setSyncing(true);
      const response = await clientService.syncSubscription(planId, billingCycle, paymentId);
      if (response.success) {
        setSubscription(response.data.subscription);
        setSyncSuccess(true);
        setError(null);
      }
    } catch (err) {
      console.error('Error syncing subscription:', err);
      setError('Failed to sync subscription. Please contact support.');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const getPlanDisplayName = (planId) => {
    const names = {
      free: 'Free',
      pro: 'Pro',
      pro_plus: 'Pro Plus',
      enterprise_starter: 'Enterprise Starter',
      enterprise_scale: 'Enterprise Scale',
    };
    return names[planId] || planId;
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto p-4 pb-20 sm:pb-4">
        {loading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-neutral-600">Processing your payment...</p>
            </div>
          </div>
        ) : error && !syncSuccess ? (
          <Card padding="lg">
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-base sm:text-xl font-bold text-neutral-900 mb-2">Processing Payment</h2>
              <p className="text-xs sm:text-sm text-neutral-600 mb-4">
                Your payment is being processed. This may take a few moments.
              </p>
              {planId && (
                <p className="text-xs sm:text-sm text-neutral-500 mb-6">
                  If your subscription doesn't update automatically, click the button below to manually sync.
                </p>
              )}
              <div className="flex flex-col sm:flex-row justify-center gap-3">
                <Button variant="ghost" onClick={() => window.location.reload()}>
                  Refresh
                </Button>
                {planId && (
                  <Button
                    variant="primary"
                    onClick={handleManualSync}
                    disabled={syncing}
                  >
                    {syncing ? 'Syncing...' : 'Sync Subscription'}
                  </Button>
                )}
                <Button variant="ghost" onClick={() => navigate('/client/subscription')}>
                  Go to Billing
                </Button>
              </div>
            </div>
          </Card>
        ) : syncSuccess ? (
          <Card padding="lg">
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-2">Subscription Synced!</h1>
              <p className="text-xs sm:text-sm text-neutral-600 mb-8">
                Your subscription has been manually synced and is now active.
              </p>
              <div className="flex justify-center gap-3">
                <Button variant="ghost" onClick={() => navigate('/client/subscription')}>
                  View Billing
                </Button>
                <Button variant="primary" onClick={() => navigate('/client/dashboard')}>
                  Go to Dashboard
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <Card padding="lg">
            <div className="text-center py-8">
              {/* Success Icon */}
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              {/* Success Message */}
              <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-2">Payment Successful!</h1>
              <p className="text-xs sm:text-sm text-neutral-600 mb-8">
                Thank you for your purchase. Your subscription has been activated.
              </p>

              {/* Subscription Details */}
              {(() => {
                const fetchedPlan = subscription?.plan || subscription?.tier;
                const planMismatch = planId && fetchedPlan && fetchedPlan !== planId;
                const expectedPlan = PLAN_DETAILS[planId] || null;
                const displayPlan = planMismatch && expectedPlan ? planId : fetchedPlan;
                const displayName = planMismatch && expectedPlan ? expectedPlan.name : getPlanDisplayName(fetchedPlan);
                const displayPrice = planMismatch && expectedPlan
                  ? (billingCycle === 'yearly' ? expectedPlan.yearlyPrice : expectedPlan.monthlyPrice)
                  : subscription?.price;
                const displayInterval = planMismatch
                  ? (billingCycle === 'yearly' ? 'year' : 'month')
                  : (subscription?.interval === 'year' ? 'year' : 'month');
                const gstAmount = planMismatch
                  ? Math.round((displayPrice * GST_RATE) / 100)
                  : (subscription?.gstAmount || Math.round((displayPrice * GST_RATE) / 100));
                const totalWithGst = planMismatch
                  ? displayPrice + gstAmount
                  : (subscription?.totalWithGst || displayPrice + gstAmount);

                return subscription && (
                  <div className="bg-neutral-50 rounded-xl p-4 sm:p-6 mb-8 text-left">
                    <h3 className="text-xs sm:text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-4">
                      Subscription Details
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-neutral-600">Plan</span>
                        <span className="font-semibold text-neutral-900">
                          {displayName}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-neutral-600">Status</span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      </div>
                      {subscription.currentPeriodEnd && (
                        <div className="flex justify-between">
                          <span className="text-neutral-600">Next Billing Date</span>
                          <span className="font-medium text-neutral-900">
                            {formatDate(subscription.currentPeriodEnd)}
                          </span>
                        </div>
                      )}
                      {displayPrice > 0 && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-neutral-600">Base Amount</span>
                            <span className="font-medium text-neutral-900">
                              ₹{displayPrice?.toLocaleString('en-IN')}/{displayInterval}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-neutral-600">GST ({GST_RATE}%)</span>
                            <span className="font-medium text-neutral-900">
                              ₹{gstAmount?.toLocaleString('en-IN')}
                            </span>
                          </div>
                          <div className="flex justify-between pt-2 border-t border-neutral-200">
                            <span className="font-semibold text-neutral-900">Total</span>
                            <span className="font-semibold text-neutral-900">
                              ₹{totalWithGst?.toLocaleString('en-IN')}/{displayInterval}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                    {planMismatch && (
                      <p className="text-xs text-amber-600 mt-3">
                        Your plan upgrade is being processed. It may take a moment to reflect everywhere.
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* Payment Reference */}
              {paymentId && (
                <p className="text-xs text-neutral-500 mb-6">
                  Payment Reference: {paymentId}
                </p>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-center gap-3">
                <Button variant="ghost" onClick={() => navigate('/client/subscription')}>
                  View Billing
                </Button>
                <Button variant="primary" onClick={() => navigate('/client/dashboard')}>
                  Go to Dashboard
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CheckoutSuccessPage;
